/**
 * DateField — a calendar-popover date input for the portal forms.
 *
 * Tapping the field opens a compact Monday-first month grid (localized weekday
 * and month labels, today outlined, the selected day filled with `$primary`).
 * Days outside [min, max] are dimmed and non-pressable. By default `min` is the
 * 1st of the current month — a request may be dated back within the month
 * (28.09.2026), never earlier — and the prev-month chevron stops at the min month.
 *
 * With an empty `value` the grid opens on TODAY's month clamped into [min, max]
 * (see `initialView`) — a far-back bound like `min="1950-01-01"` must not strand
 * the user in January 1950. Tapping the "Month Year" header swaps the day grid
 * for a scrollable 4-column year list (min year → max year, or ten years ahead
 * when `max` is open); picking a year returns to the day grid on the same month,
 * clamped back into range. The mode resets to the day grid on every re-open.
 *
 * Used by the EDO create forms. For the time-off "end date", pass `disabled` to
 * render a locked, read-only mirror of the start date (an отгул is always a
 * single day, ≤ 8 h — see TimeOffForm in app/(portal)/processes.tsx).
 *
 * Outside-click dismissal mirrors LanguageMenu: a full-bleed Backdrop (`fixed`
 * on web, `absolute` on native) sits under the absolutely-positioned panel.
 */
import { useRef, useState } from 'react'
import { ScrollView } from 'react-native'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, type Lang } from '../i18n'
import { pad2 } from '../lib/date'
import { palette } from '../theme/palette'
import { Backdrop } from './Backdrop'
import { Icon } from './Icon'

/** Monday-first weekday initials (mirrors app/(portal)/calendar.tsx). */
const WEEK_DAYS: Record<Lang, readonly string[]> = {
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
  az: ['B.e', 'Ç.a', 'Çər', 'C.a', 'Cüm', 'Şən', 'Baz'],
  ka: ['ორ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ', 'კვი'],
}

/** Названия месяцев (именительный падеж) — и для выбора отчётного месяца в формах. */
export const MONTHS: Record<Lang, readonly string[]> = {
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  az: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'],
  ka: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'],
}

const ymd = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`

/** Local "today" as YYYY-MM-DD — read on demand so the bound rolls at midnight. */
export function todayYmd(): string {
  const n = new Date()
  return ymd(n.getFullYear(), n.getMonth(), n.getDate())
}

/** The 1st of the month of `today` (default — the current month): the default lower bound. */
export function monthStartYmd(today: string = todayYmd()): string {
  return `${today.slice(0, 8)}01`
}

interface Parsed {
  y: number
  m: number
  d: number
}
function parseYmd(s: string): Parsed | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null
}

/** Monday-first weeks; nulls pad the leading/trailing edges of the month. */
function monthMatrix(year: number, month: number): (number | null)[][] {
  const startOffset = (new Date(year, month, 1).getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: (number | null)[] = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)
  const weeks: (number | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

/** Clamp a {year, month} pair into the [min, max] month range (month index math). */
export function clampView(y: number, m: number, min: Parsed | null, max: Parsed | null): { y: number; m: number } {
  const lo = min ? min.y * 12 + min.m : -Infinity
  const hi = max ? max.y * 12 + max.m : Infinity
  const k = Math.max(lo, Math.min(hi, y * 12 + m))
  return { y: Math.floor(k / 12), m: k % 12 }
}

/**
 * Month the grid opens on: the selected value's month, else today's month
 * clamped into [minDay, max]. Keeps a far-back `min` (PersonEditor passes
 * 1950-01-01) from stranding an empty field hundreds of months in the past.
 */
export function initialView(value: string, minDay: string, max: string | undefined, today: string): { y: number; m: number } {
  const p = parseYmd(value)
  if (p) return { y: p.y, m: p.m }
  const t = parseYmd(today)!
  return clampView(t.y, t.m, parseYmd(minDay), max ? parseYmd(max) : null)
}

const YEAR_ROW_H = 34
const YEAR_COLS = 4

export interface DateFieldProps {
  /** Selected value as `YYYY-MM-DD`, or '' when empty. */
  value: string
  onChange: (next: string) => void
  /** Earliest selectable day (`YYYY-MM-DD`). Defaults to the 1st of the current month. */
  min?: string
  /** Latest selectable day (`YYYY-MM-DD`). */
  max?: string
  placeholder?: string
  /** Locked, read-only mirror — shows the value but never opens the calendar. */
  disabled?: boolean
  /** Highlight as a still-empty required field — navy accent border until filled. */
  highlight?: boolean
  /** Horizontal anchor of the panel relative to the field. */
  align?: 'left' | 'right'
  /** Per-day disable predicate (`YYYY-MM-DD` → true = dimmed, non-pressable). Used to block
   *  non-working days (weekends/holidays) in the time-off form. Days already outside [min,max]
   *  are disabled regardless. */
  isDayDisabled?: (ymd: string) => boolean
  /** Текст в поле вместо `value` (напр. «Сегодня, 29.10» в «Состоянии»); по умолчанию — сам value. */
  display?: string
}

export function DateField({
  value,
  onChange,
  min,
  max,
  placeholder = 'ГГГГ-ММ-ДД',
  disabled,
  highlight,
  align = 'left',
  isDayDisabled,
  display,
}: DateFieldProps) {
  const { lang, t } = useApp()
  const minDay = min ?? monthStartYmd()
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'day' | 'year'>('day')
  const yearsRef = useRef<ScrollView>(null)

  // Month on screen — the value's month, else today clamped into [min, max].
  const [view, setView] = useState(() => initialView(value, minDay, max, todayYmd()))

  const openPanel = () => {
    setView(initialView(value, minDay, max, todayYmd())) // always re-open on a relevant month
    setMode('day')
    setOpen(true)
  }

  const shift = (delta: number) => {
    const d = new Date(view.y, view.m + delta, 1)
    setView({ y: d.getFullYear(), m: d.getMonth() })
  }

  // Stop month paging at the bounds so off-range months stay unreachable.
  const minP = parseYmd(minDay)
  const maxP = max ? parseYmd(max) : null
  const atMin = !!minP && (view.y < minP.y || (view.y === minP.y && view.m <= minP.m))
  const atMax = !!maxP && (view.y > maxP.y || (view.y === maxP.y && view.m >= maxP.m))

  const weeks = monthMatrix(view.y, view.m)
  const today = todayYmd()

  // Year list — bounded by min/max, else ten years past the later of today/min.
  const minYear = minP?.y ?? view.y
  const maxYear = maxP?.y ?? Math.max(+today.slice(0, 4), minYear) + 10
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i)

  // Keep the month, move the year, then clamp the pair back into [min, max].
  const pickYear = (y: number) => {
    setView(clampView(y, view.m, minP, maxP))
    setMode('day')
  }

  return (
    <Stack position="relative">
      {/* trigger — styled to match the form Inputs (height 40, $bgLayout chrome) */}
      <XStack
        onPress={disabled ? undefined : openPanel}
        cursor={disabled ? 'default' : 'pointer'}
        height={40}
        alignItems="center"
        gap={8}
        paddingHorizontal={11}
        backgroundColor="$bgLayout"
        borderWidth={1}
        borderColor={open || highlight ? '$primary' : '$borderColor'}
        borderRadius={7}
        opacity={disabled ? 0.6 : 1}
        role="button"
        aria-disabled={!!disabled}
        aria-expanded={open}
      >
        <Text flex={1} fontSize={14} color={value ? '$color' : '$tertiary'}>
          {/* t() по строке: «ГГГГ-ММ-ДД» (дефолт и все вызовы) берётся из AZ_DB для az. */}
          {display ?? (value || t(placeholder))}
        </Text>
        <Icon name="calendar" size={16} color="$muted" />
      </XStack>

      {open && !disabled && (
        <>
          <Backdrop onPress={() => setOpen(false)} />
          <YStack
            position="absolute"
            top={46}
            {...(align === 'left' ? { left: 0 } : { right: 0 })}
            width={264}
            zIndex={50}
            padding={10}
            gap={6}
            borderWidth={1}
            borderRadius={10}
            backgroundColor="$bgElevated"
            borderColor="$borderSecondary"
            shadowColor={palette.shadow}
            shadowOpacity={0.18}
            shadowRadius={20}
            shadowOffset={{ width: 0, height: 14 }}
          >
            {/* month nav — the title toggles the year list; chevrons are day-mode only */}
            <XStack alignItems="center" justifyContent="space-between" paddingHorizontal={2} paddingBottom={2}>
              {mode === 'day' && (
                <Stack
                  onPress={atMin ? undefined : () => shift(-1)}
                  cursor={atMin ? 'default' : 'pointer'}
                  opacity={atMin ? 0.3 : 1}
                  padding={4}
                  borderRadius={6}
                  hoverStyle={atMin ? undefined : { backgroundColor: '$backgroundHover' }}
                  role="button"
                  aria-disabled={atMin}
                >
                  <Icon name="chevron-left" size={18} color="$muted" />
                </Stack>
              )}
              <Stack
                flex={1}
                alignItems="center"
                paddingVertical={4}
                borderRadius={7}
                cursor="pointer"
                hoverStyle={{ backgroundColor: '$backgroundHover' }}
                onPress={() => setMode(mode === 'day' ? 'year' : 'day')}
                role="button"
                accessibilityLabel={`${MONTHS[lang][view.m]} ${view.y}`}
                aria-expanded={mode === 'year'}
              >
                <Text fontSize={13} fontWeight="700" color="$color">
                  {mode === 'day' ? `${MONTHS[lang][view.m]} ${view.y}` : view.y}
                </Text>
              </Stack>
              {mode === 'day' && (
                <Stack
                  onPress={atMax ? undefined : () => shift(1)}
                  cursor={atMax ? 'default' : 'pointer'}
                  opacity={atMax ? 0.3 : 1}
                  padding={4}
                  borderRadius={6}
                  hoverStyle={atMax ? undefined : { backgroundColor: '$backgroundHover' }}
                  role="button"
                  aria-disabled={atMax}
                >
                  <Icon name="chevron-right" size={18} color="$muted" />
                </Stack>
              )}
            </XStack>

            {mode === 'year' ? (
              <ScrollView
                ref={yearsRef}
                style={{ maxHeight: 6 * YEAR_ROW_H }}
                showsVerticalScrollIndicator={false}
                // scroll the viewed year into view once the rows are laid out
                onContentSizeChange={() =>
                  yearsRef.current?.scrollTo({
                    y: Math.max(0, (Math.floor(Math.max(0, view.y - minYear) / YEAR_COLS) - 2) * YEAR_ROW_H),
                    animated: false,
                  })
                }
              >
                <XStack flexWrap="wrap">
                  {years.map((y) => {
                    const sel = y === view.y
                    return (
                      <Stack
                        key={y}
                        width={`${100 / YEAR_COLS}%`}
                        height={YEAR_ROW_H}
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={7}
                        backgroundColor={sel ? '$primary' : 'transparent'}
                        cursor="pointer"
                        hoverStyle={sel ? undefined : { backgroundColor: '$backgroundHover' }}
                        onPress={() => pickYear(y)}
                        role="button"
                        aria-selected={sel}
                      >
                        <Text fontSize={13} fontWeight={sel ? '700' : '500'} color={sel ? '$onPrimary' : '$color'}>
                          {y}
                        </Text>
                      </Stack>
                    )
                  })}
                </XStack>
              </ScrollView>
            ) : (
              <>
              {/* weekday header */}
              <XStack gap={2}>
                {WEEK_DAYS[lang].map((w, i) => (
                  <Stack key={i} flex={1} alignItems="center" paddingVertical={3}>
                    <Text fontSize={10} fontWeight="600" color={i >= 5 ? '$tertiary' : '$muted'}>
                      {w}
                    </Text>
                  </Stack>
                ))}
              </XStack>

              {/* day grid */}
              {weeks.map((week, wi) => (
                <XStack key={wi} gap={2}>
                  {week.map((day, di) => {
                    if (day == null) return <Stack key={di} flex={1} aspectRatio={1} />
                    const cur = ymd(view.y, view.m, day)
                    const off =
                      cur < minDay || (!!max && cur > max) || (isDayDisabled?.(cur) ?? false)
                    const selected = cur === value
                    const isToday = cur === today
                    return (
                      <Stack
                        key={di}
                        flex={1}
                        aspectRatio={1}
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={7}
                        backgroundColor={selected ? '$primary' : 'transparent'}
                        borderWidth={!selected && isToday ? 1 : 0}
                        borderColor={isToday ? '$primary' : 'transparent'}
                        opacity={off ? 0.32 : 1}
                        cursor={off ? 'default' : 'pointer'}
                        hoverStyle={off || selected ? undefined : { backgroundColor: '$backgroundHover' }}
                        onPress={
                          off
                            ? undefined
                            : () => {
                                onChange(cur)
                                setOpen(false)
                              }
                        }
                        role="button"
                        aria-disabled={off}
                        aria-selected={selected}
                      >
                        <Text fontSize={13} fontWeight={selected ? '700' : '500'} color={selected ? '$onPrimary' : '$color'}>
                          {day}
                        </Text>
                      </Stack>
                    )
                  })}
                </XStack>
              ))}
              </>
            )}
          </YStack>
        </>
      )}
    </Stack>
  )
}
